import { useCallback, useReducer, Reducer } from 'react'
import { generateApiErrorPayload } from 'api-client/helpers'
import { useUploadProgress } from 'core/context/uploadFileProgressContext'
import { trackEvent } from 'utils/tracking'

interface UpdateState<R> {
  [key: string]: {
    updating: boolean
    success: boolean
    error: ReturnType<typeof generateApiErrorPayload> | null
    result: R | null
    progress?: number
  }
}

interface UpdateReducerAction<R> {
  type: 'startUpdate' | 'endUpdate' | 'setError' | 'setProgress'
  key: string
  payload?: any
}

const trackUploadSuccess = (state, key) => {
  // From what I can tell the only way to track success is if state[key].progress === 100
  if (state[key]?.progress === 100) {
    trackEvent('File Upload - Success')
  } else {
    trackEvent('File Upload - Failed')
  }
}

const updateReducer = <R>(
  state: UpdateState<R>,
  action: UpdateReducerAction<R>,
): UpdateState<R> => {
  const { key, type, payload } = action
  // Return current state for unknown action types
  if (!['startUpdate', 'endUpdate', 'setError', 'setProgress'].includes(type)) {
    return state
  }
  if (type === 'endUpdate') {
    trackUploadSuccess(state, key)
  }
  return {
    ...state,
    [key]: {
      ...state[key],
      updating: type === 'startUpdate' ? true : false,
      success: type === 'endUpdate' ? payload : state[key]?.success ?? false,
      error: type === 'setError' ? payload : state[key]?.error ?? null,
      result: type === 'endUpdate' ? payload : state[key]?.result ?? null,
      progress: type === 'setProgress' ? payload : state[key]?.progress ?? 0,
    },
  }
}

const useUpdateActionsWithProgress = <R>() => {
  const [state, dispatch] = useReducer<Reducer<UpdateState<R>, UpdateReducerAction<R>>>(
    updateReducer,
    {},
  )
  const { updateFileStatus } = useUploadProgress()

  const update = useCallback(
    async (key: string, actionInstance: any, params: any) => {
      dispatch({ type: 'startUpdate', key })
      params.abortController = new AbortController()
      updateFileStatus({
        id: params.id,
        fileName: params.name,
        progress: 0,
        error: null,
        result: null,
        payload: params.imagePayload,
        retry: null,
        reset: params.abortController,
      })

      const handleProgress = (progressEvent: ProgressEvent) => {
        if (!params.abortController?.signal.aborted) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          dispatch({ type: 'setProgress', key, payload: percent })
          updateFileStatus({
            id: params.id,
            fileName: params.name,
            progress: percent,
            payload: params.imagePayload,
            error: null,
            result: null,
            retry: null,
            reset: params.abortController,
          })
        }
      }

      const handleError = (error) => {
        const apiError = generateApiErrorPayload(error)
        dispatch({ type: 'setError', key, payload: apiError })

        updateFileStatus({
          id: params.id,
          fileName: params.name,
          progress: 0,
          error: apiError,
          result: null,
          payload: params.imagePayload,
          retry: params.id
            ? async (imageId: string) => {
                if (imageId) {
                  await update(key, actionInstance, {
                    ...params,
                    id: imageId,
                  })
                }
              }
            : null,
          reset: params.abortController,
        })

        return { response: null, success: false, error: apiError }
      }

      try {
        const result = await actionInstance.call({
          ...params,
          signal: params.abortController.signal,
          onUploadProgress: handleProgress,
          onError: (error: any) => {
            if (error?.response?.status !== 404) handleError(error)
          },
        })

        dispatch({ type: 'endUpdate', key, payload: result })
        return { response: result, success: true, error: null }
      } catch (error) {
        return handleError(error)
      }
    },
    [dispatch, updateFileStatus],
  )

  return { state, update }
}

export default useUpdateActionsWithProgress
